import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import {
  Users,
  DollarSign,
  Calendar,
  Heart,
  TrendingUp,
  TrendingDown,
  Download,
  Plus,
  Loader2,
} from 'lucide-react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'
import { getChurchCurrencySymbol } from '@/lib/currencyService'
import { useDemoDashboard } from '@/hooks/useDemoData'

interface DashboardMetrics {
  totalMembers: number
  monthlyGiving: number
  weeklyAttendance: number
  soulsWon: number
}

interface DashboardProps {
  user: AdminUser | null
}

export default function Dashboard({ user }: DashboardProps) {
  const navigate = useNavigate()
  const { isDemoMode, data: demoData } = useDemoDashboard()

  useEffect(() => {
    document.title = 'Dashboard - FaithYoke'
  }, [])

  const [loading, setLoading] = useState(true)
  const [currencySymbol, setCurrencySymbol] = useState('$')
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalMembers: 0,
    monthlyGiving: 0,
    weeklyAttendance: 0,
    soulsWon: 0,
  })
  const [recentMembers, setRecentMembers] = useState<any[]>([])
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([])
  const [givingData, setGivingData] = useState<any[]>([])
  const [attendanceData, setAttendanceData] = useState<any[]>([])
  const [recentActivities, setRecentActivities] = useState<any[]>([])

  useEffect(() => {
    // If demo mode is enabled, use demo data
    if (isDemoMode && demoData) {
      setMetrics({
        totalMembers: demoData.metrics.totalMembers,
        monthlyGiving: demoData.metrics.monthlyGiving,
        weeklyAttendance: demoData.metrics.weeklyAttendance,
        soulsWon: demoData.metrics.soulsWon,
      })
      setCurrencySymbol(demoData.currencySymbol)
      setRecentMembers(demoData.recentMembers)
      setUpcomingEvents(demoData.upcomingEvents)
      setGivingData(demoData.givingData)
      setAttendanceData(demoData.attendanceData)
      setRecentActivities(demoData.recentActivities)
      setLoading(false)
      return
    }

    loadDashboardData()
  }, [isDemoMode, demoData])

  function getTimeAgo(timestamp: string): string {
    const now = new Date()
    const past = new Date(timestamp)
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return past.toLocaleDateString()
  }

  function getActivityIcon(type: string) {
    switch (type) {
      case 'member': return 'bg-blue-500'
      case 'giving': return 'bg-green-500'
      case 'event': return 'bg-sky-500'
      case 'attendance': return 'bg-orange-500'
      case 'prayer': return 'bg-cyan-400'
      default: return 'bg-primary'
    }
  }

  async function loadDashboardData() {
    try {
      setLoading(true)

      // Get church ID from current user session
      const { data: { user: authUser } } = await supabase.auth.getUser()
      console.log('🔍 [DASHBOARD] Auth user:', authUser?.id)

      // Get user's church ID from profile
      let churchId: string | undefined

      if (authUser) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('church_id')
          .eq('id', authUser.id)
          .single()

        churchId = profile?.church_id
        console.log('🔍 [DASHBOARD] Church ID from profile:', churchId)
      }

      if (!churchId) {
        console.warn('No church found. Please set up church data.')
        setLoading(false)
        return
      }

      // Get date ranges
      const today = new Date()
      const sevenDaysAgo = new Date(today)
      sevenDaysAgo.setDate(today.getDate() - 7)
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

      // Load church currency
      const symbol = await getChurchCurrencySymbol(churchId)
      setCurrencySymbol(symbol)

      // Load metrics in parallel
      const [membersResult, givingResult, eventsResult, evangelismResult, weeklyAttendanceResult] = await Promise.all([
        supabase.from('members').select('*', { count: 'exact' }).eq('church_id', churchId),
        supabase.from('contributions').select('amount').eq('church_id', churchId).gte('created_at', firstOfMonth.toISOString()),
        supabase.from('events').select('*').eq('church_id', churchId).gte('start_datetime', new Date().toISOString()).order('start_datetime', { ascending: true }).limit(3),
        supabase.from('evangelism_contacts').select('*', { count: 'exact' }).eq('church_id', churchId).in('stage', ['believer', 'baptized', 'disciple']),
        supabase.from('attendance').select('id, member_id, members!inner(church_id)').eq('members.church_id', churchId).gte('created_at', sevenDaysAgo.toISOString()),
      ])

      console.log('🔍 [DASHBOARD] Members result:', { count: membersResult.count, error: membersResult.error, dataLength: membersResult.data?.length })
      console.log('🔍 [DASHBOARD] Evangelism result:', { count: evangelismResult.count, error: evangelismResult.error, dataLength: evangelismResult.data?.length })

      // Calculate metrics
      const totalMembers = membersResult.count || 0
      const monthlyGiving = givingResult.data?.reduce((sum, g) => sum + (g.amount || 0), 0) || 0
      const soulsWon = evangelismResult.count || 0
      const weeklyAttendance = weeklyAttendanceResult.data?.length || 0

      console.log('🔍 [DASHBOARD] Calculated metrics:', { totalMembers, soulsWon, monthlyGiving, weeklyAttendance })

      setMetrics({
        totalMembers,
        monthlyGiving,
        weeklyAttendance,
        soulsWon,
      })

      // Set recent members (last 4)
      const recentMembersData = membersResult.data?.slice(-4).reverse() || []
      setRecentMembers(recentMembersData)

      // Set upcoming events
      setUpcomingEvents(eventsResult.data || [])

      // Load giving data for chart (last 6 months)
      const sixMonthsAgo = new Date()
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

      const { data: givingHistory } = await supabase
        .from('contributions')
        .select('amount, created_at')
        .eq('church_id', churchId)
        .gte('created_at', sixMonthsAgo.toISOString())

      // Load attendance data for chart (last 6 months)
      const { data: attendanceHistory } = await supabase
        .from('attendance')
        .select('created_at, member_id, members!inner(church_id)')
        .eq('members.church_id', churchId)
        .gte('created_at', sixMonthsAgo.toISOString())

      // Group giving by month
      const givingByMonth = (givingHistory || []).reduce((acc, g) => {
        const month = new Date(g.created_at).toLocaleString('default', { month: 'short' })
        acc[month] = (acc[month] || 0) + g.amount
        return acc
      }, {} as Record<string, number>)

      // Group attendance by month
      const attendanceByMonth = (attendanceHistory || []).reduce((acc, a) => {
        const month = new Date(a.created_at).toLocaleString('default', { month: 'short' })
        acc[month] = (acc[month] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      // Get all months in the last 6 months
      const months: string[] = []
      for (let i = 5; i >= 0; i--) {
        const date = new Date()
        date.setMonth(date.getMonth() - i)
        months.push(date.toLocaleString('default', { month: 'short' }))
      }

      const chartData = months.map(month => ({
        month,
        giving: givingByMonth[month] || 0,
        attendance: attendanceByMonth[month] || 0,
      }))

      setGivingData(chartData)
      setAttendanceData(chartData)

      // Load recent activities
      const threeDaysAgo = new Date()
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3)

      const [recentMembersActivity, recentGivingActivity, recentEventsActivity, recentAttendanceActivity, recentPrayersActivity] = await Promise.all([
        supabase.from('members').select('preferred_name, created_at').eq('church_id', churchId).gte('created_at', threeDaysAgo.toISOString()).order('created_at', { ascending: false }).limit(3),
        supabase.from('contributions').select('amount, created_at, member_id, members(preferred_name)').eq('church_id', churchId).gte('created_at', threeDaysAgo.toISOString()).order('created_at', { ascending: false }).limit(3),
        supabase.from('events').select('title, created_at').eq('church_id', churchId).gte('created_at', threeDaysAgo.toISOString()).order('created_at', { ascending: false }).limit(3),
        supabase.from('attendance').select('created_at, member_id, members!inner(preferred_name, church_id)').eq('members.church_id', churchId).gte('created_at', threeDaysAgo.toISOString()).order('created_at', { ascending: false }).limit(3),
        supabase.from('prayer_requests').select('title, created_at').eq('church_id', churchId).gte('created_at', threeDaysAgo.toISOString()).order('created_at', { ascending: false }).limit(2),
      ])

      // Combine and format activities
      const activities: any[] = []

      // Add member activities
      recentMembersActivity.data?.forEach(member => {
        activities.push({
          type: 'member',
          message: `${member.preferred_name} joined the church`,
          timestamp: member.created_at
        })
      })

      // Add giving activities
      recentGivingActivity.data?.forEach(giving => {
        const member = (giving.members as any)
        const memberName = member?.preferred_name || 'Anonymous'
        activities.push({
          type: 'giving',
          message: `${memberName} donated ${symbol}${giving.amount.toLocaleString()}`,
          timestamp: giving.created_at
        })
      })

      // Add event activities
      recentEventsActivity.data?.forEach(event => {
        activities.push({
          type: 'event',
          message: `New event created: ${event.title}`,
          timestamp: event.created_at
        })
      })

      // Add attendance activities
      recentAttendanceActivity.data?.forEach(attendance => {
        const memberName = (attendance.members as any)?.preferred_name || 'A member'
        activities.push({
          type: 'attendance',
          message: `${memberName} checked in to service`,
          timestamp: attendance.created_at
        })
      })

      // Add prayer request activities
      recentPrayersActivity.data?.forEach(prayer => {
        activities.push({
          type: 'prayer',
          message: `New prayer request: ${prayer.title}`,
          timestamp: prayer.created_at
        })
      })

      // Sort by timestamp and take the most recent 10
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      setRecentActivities(activities.slice(0, 10))

    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  const metricsDisplay = [
    {
      title: 'Total Members',
      value: metrics.totalMembers.toLocaleString(),
      change: '+0%',
      trend: 'up',
      icon: Users,
    },
    {
      title: 'Monthly Giving',
      value: `${currencySymbol}${metrics.monthlyGiving.toLocaleString()}`,
      change: '+0%',
      trend: 'up',
      icon: DollarSign,
    },
    {
      title: 'Weekly Attendance',
      value: metrics.weeklyAttendance.toString(),
      change: '+0%',
      trend: 'up',
      icon: Calendar,
    },
    {
      title: 'Souls Won',
      value: metrics.soulsWon.toString(),
      change: '+0%',
      trend: 'up',
      icon: Heart,
    },
  ]
  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {user ? `${user.firstName} ${user.lastName}` : 'Admin User'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/members')}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button size="sm" onClick={() => navigate('/members')}>
            <Plus className="mr-2 h-4 w-4" />
            Add Member
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {metricsDisplay.map((metric, index) => (
          <Card
            key={index}
            className={metric.title === 'Weekly Attendance' ? 'cursor-pointer hover:border-primary transition-colors' : ''}
            onClick={() => metric.title === 'Weekly Attendance' && navigate('/attendance')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
              <metric.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metric.value}</div>
              <div className="flex items-center text-xs text-muted-foreground">
                {metric.trend === 'up' ? (
                  <TrendingUp className="mr-1 h-3 w-3 text-green-500" />
                ) : (
                  <TrendingDown className="mr-1 h-3 w-3 text-red-500" />
                )}
                <span className={metric.trend === 'up' ? 'text-green-500' : 'text-red-500'}>
                  {metric.change}
                </span>
                <span className="ml-1">from last month</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => navigate('/attendance')}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Attendance Overview</CardTitle>
                <CardDescription>Monthly attendance trends - Click to view details</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate('/attendance') }}>
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                attendance: {
                  label: 'Attendance',
                  color: 'hsl(var(--primary))',
                },
              }}
              className="h-[300px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={attendanceData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="attendance"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary))"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Giving Trends</CardTitle>
            <CardDescription>Monthly giving statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                giving: {
                  label: 'Giving',
                  color: 'hsl(var(--primary))',
                },
              }}
              className="h-[300px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={givingData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="giving"
                    fill="hsl(var(--primary))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Section */}
      <Tabs defaultValue="members" className="space-y-4">
        <TabsList>
          <TabsTrigger value="members">Recent Members</TabsTrigger>
          <TabsTrigger value="events">Upcoming Events</TabsTrigger>
          <TabsTrigger value="activities">Recent Activities</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Members</CardTitle>
              <CardDescription>New members who joined recently</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Join Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentMembers.length > 0 ? (
                    recentMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell className="font-medium">{member.preferred_name}</TableCell>
                        <TableCell>{member.email || 'No email'}</TableCell>
                        <TableCell>{new Date(member.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant="default">
                            {member.role}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        No members yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Events</CardTitle>
              <CardDescription>Events scheduled for this week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map((event, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{event.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(event.start_datetime).toLocaleString()}
                        </p>
                        {event.location && (
                          <p className="text-xs text-muted-foreground">{event.location}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge>{event.event_type}</Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No upcoming events
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activities</CardTitle>
              <CardDescription>Latest activities in the church (last 3 days)</CardDescription>
            </CardHeader>
            <CardContent>
              {recentActivities.length > 0 ? (
                <div className="space-y-4">
                  {recentActivities.map((activity, index) => (
                    <div key={index} className="flex gap-3">
                      <div className={`h-2 w-2 rounded-full ${getActivityIcon(activity.type)} mt-1.5`} />
                      <div className="flex-1">
                        <p className="text-sm">{activity.message}</p>
                        <p className="text-xs text-muted-foreground">{getTimeAgo(activity.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No recent activities
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}